/* eslint-disable @typescript-eslint/no-extraneous-class */
import Papa from 'papaparse'
import { parsers } from '@/parsers'
import type { EmptyInputTypes, InputTypes } from '@/parsers'
import { uploadTables, type UploadTableName } from '../types'
import { booleanFilterTypeGuard } from '@/util/helpers'

export interface FileProcessingResult {
  tableName: UploadTableName
  inputData: InputTypes[]
  errors: Record<string, string>
}

export class FileProcessingService {
  static async processFile(file: File): Promise<FileProcessingResult> {
    const fileName = file.name
    const errors: Record<string, string> = {}

    // Determine table name from filename
    const tableName = uploadTables.find((uploadTable) =>
      fileName.toLowerCase().startsWith(uploadTable.toLowerCase()),
    ) as UploadTableName | undefined

    if (!tableName) {
      throw new Error(
        `Filename ${fileName} is invalid: Filename must start with: "${Object.keys(
          parsers,
        ).join('", or "')}"`,
      )
    }

    const parser = parsers[tableName]
    if (!parser) {
      throw new Error(`No parser found for table ${tableName}`)
    }

    // Parse CSV file
    const parseResult = await new Promise<
      Papa.ParseResult<InputTypes | EmptyInputTypes>
    >((resolve) => {
      Papa.parse<InputTypes | EmptyInputTypes>(file, {
        header: true,
        complete: resolve,
      })
    })

    // Process and validate rows
    const inputData = parseResult.data
      .filter((value): value is InputTypes =>
        Object.values(value).some(Boolean),
      )
      .map((value, i) => {
        try {
          // Trim row keys to avoid errors from extra whitespace
          const trimmedValue = Object.keys(value).reduce<
            Record<string, unknown>
          >((acc, key) => {
            acc[key.trim()] = value[key as keyof typeof value]
            return acc
          }, {})
          return parser.validateSync(trimmedValue)
        } catch (e) {
          errors[i] = `Row ${i + 1}: ${(e as Error).message}`
          return null
        }
      })
      .filter(booleanFilterTypeGuard)

    if (inputData.length === 0 && Object.keys(errors).length === 0) {
      throw new Error('No data found in file')
    }

    return {
      tableName,
      inputData,
      errors,
    }
  }
}
